import {KeyboardTimePicker, KeyboardTimePickerProps} from '@material-ui/pickers';
import {MuiPickersUtilsProvider} from '@material-ui/pickers/MuiPickersUtilsProvider';
import React from 'react';
import {Field, FieldProps, FieldRenderProps} from 'react-final-form';

type SmartKeyboardTimePickerProps = KeyboardTimePickerProps;

export const SmartKeyboardTimePicker : React.FC<SmartKeyboardTimePickerProps> = (
    props : SmartKeyboardTimePickerProps
) => {
    const {onBlur, ...rest} = props;
    const {onChange, value} = rest;

    return (
        <KeyboardTimePicker
            onKeyUp={event => {
                if (event.key === 'n') {
                    const now = new Date();
                    now.setSeconds(0);
                    now.setMilliseconds(0);
                    onChange(now);
                    event.preventDefault();
                }
            }}
            onBlur={event => {
                if (onBlur) {
                    onBlur(event);
                }

                if (!value) {
                    return;
                }

                if (!isNaN((value as Date).valueOf())) {
                    return;
                }

                const cleanedString = event.currentTarget.value.replace(/[^\dap]/gi, '');
                const match = cleanedString.match(/^(\d{1,2})(\d{2})?([apAP])?$/);

                if (!match) {
                    return;
                }

                const hours = parseInt(match[1], 10);
                const minutes = match[2] === undefined ? 0 : parseInt(match[2], 10);
                const ampm = match[3];

                if (hours > 23 || minutes > 59 || (hours > 12 && ampm)) {
                    return;
                }

                const date = new Date();

                if (!ampm && hours < 7 && hours > 0) {
                    date.setHours(hours + 12);
                } else if (hours < 12 && (ampm === 'p' || ampm === 'P')) {
                    date.setHours(hours + 12);
                } else if (hours === 12 && (ampm === 'a' || ampm === 'A')) {
                    date.setHours(0);
                } else {
                    date.setHours(hours);
                }

                date.setMinutes(minutes);
                date.setSeconds(0);
                date.setMilliseconds(0);
                onChange(date);
            }}
            {...rest}
        />
    );
};

type RffSmartKeyboardTimePickerProps = Partial<SmartKeyboardTimePickerProps> & {
    name : string;
    dateFunsUtils ?: unknown;
    fieldProps ?: Partial<FieldProps<string, FieldRenderProps<string>>>;
};

export const RffSmartKeyboardTimePicker : React.FC<RffSmartKeyboardTimePickerProps> = (
    props : RffSmartKeyboardTimePickerProps
) => {
    const {name, fieldProps, ...rest} = props;

    return (
        <Field
            name={name}
            render={fieldRenderProps => <SmartKeyboardTimePickerWrapper {...fieldRenderProps} {...rest}/>}
            {...fieldProps}
        />
    );
};

type KeyboardTimePickerWrapperProps = FieldRenderProps<string> & {
    dateFunsUtils ?: unknown;
};

const SmartKeyboardTimePickerWrapper : React.FC<KeyboardTimePickerWrapperProps> = (
    props : KeyboardTimePickerWrapperProps
) => {
    const {
        input: {name, onChange, value, ...restInput},
        meta,
        dateFunsUtils,
        ...rest
    } = props;

    const {helperText, ...lessrest} = rest;
    const showError = ((meta.submitError && !meta.dirtySinceLastSubmit) || meta.error) && meta.touched;

    const component = (
        <SmartKeyboardTimePicker
            fullWidth
            autoOk
            helperText={showError ? meta.error || meta.submitError : helperText}
            error={showError}
            margin="normal"
            onChange={onChange}
            name={name}
            value={value === '' ? null : value}
            {...lessrest}
            inputProps={restInput}
        />
    );

    return dateFunsUtils ? (
        <MuiPickersUtilsProvider utils={dateFunsUtils}>{component}</MuiPickersUtilsProvider>
    ) : component;
};
